import { useGetGymNotificationByUserIdQuery } from "../../../../api/queries";
import { useParams } from "react-router-dom";
import MainTitle from "../../../../components/ui/MainTitle/MainTitle";
import { useTranslation } from "react-i18next";
import React, { useMemo, useRef, useState } from "react";
import { Form, FormInstance, Table, TableProps } from "antd";
import { IUserGymNotificationWithGymInfo } from "../../../../models/GymNotification";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import dots from "../../../../assets/images/icons/dots.svg";
import editIcon from "../../../../assets/images/icons/edit-dark.svg";
import basketIcon from "../../../../assets/images/icons/basket-black.svg";
import FormItemDatePicker from "../../../../components/ui/FormItemDatePicker/FormItemDatePicker";
import CustomButton from "../../../../components/ui/CustomButton/CustomButton";
import searchIconWhite from "../../../../assets/images/icons/search-icon-white.svg";
import { useUserGymNotificationMutations } from "../../../../api/mutations";
import { openSuccessMessage } from "../../../../store/message/messageSlice";
import { handleError } from "../../../../helper/errorHandler";
import useAppDispatch from "../../../../hooks/useAppDispatch";

const ClientGymNotification = () => {
  const dispatch = useAppDispatch();
  const formRef = useRef<FormInstance>(null);
  const { t } = useTranslation();
  const { id } = useParams();
  const [filters, setFilters] = useState<{ from: string | null; to: string | null }>({
    from: null,
    to: null,
  });
  const [tableParams, setTableParams] = useState<{ currentPage: number; pageSize: number }>({
    currentPage: 1,
    pageSize: 10,
  });
  const {
    data = { items: [], total: 0 },
    isLoading,
    refetch,
  } = useGetGymNotificationByUserIdQuery({ ...tableParams, userId: id!, queryOptions: filters });
  const { deleteById, updateSentDate } = useUserGymNotificationMutations();

  const totalCount = useMemo(() => {
    if (!data) return 0;
    return data.total;
  }, [data]);

  const handleTableChange: TableProps<IUserGymNotificationWithGymInfo>["onChange"] = (
    pagination
  ) => {
    setTableParams({
      currentPage: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  const remove = async (id: string) => {
    deleteById(id, {
      onSuccess: () => {
        refetch();
        dispatch(openSuccessMessage(t("GymNotificationRemovedSuccessfully")));
      },
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  const updateSentDateHandler = (id: string) => {
    updateSentDate(id, {
      onSuccess: () => {
        refetch();
        dispatch(openSuccessMessage(t("SentDateChangedSuccessfully")));
      },
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  const onFinish = (values: { from: Date | undefined; to: Date | undefined }) => {
    const model = {
      from: values.from ? values.from.toISOString() : null,
      to: values.to ? values.to.toISOString() : null,
    };
    setFilters(model);
  };

  const columns: ColumnsType<IUserGymNotificationWithGymInfo> = [
    {
      title: t("GymName"),
      render: (data: IUserGymNotificationWithGymInfo) => {
        return data.gymListing.licenseGymName;
      },
    },
    {
      title: t("Location"),
      render: (data: IUserGymNotificationWithGymInfo) => {
        return `${data.gymListing.country} ${data.gymListing.city}`;
      },
    },
    {
      title: t("DataSubscribed"),
      render: (data: IUserGymNotificationWithGymInfo) => {
        return dayjs(data.createdAt).format("MMM D, YYYY");
      },
    },
    {
      title: t("Status"),
      render: (data: IUserGymNotificationWithGymInfo) => {
        if (data.status === "COMING_SOON") {
          return <span className={"status coming-soon"}>{t("ComingSoon")}</span>;
        } else {
          return <span className={"status approved"}>{t("Opened")}</span>;
        }
      },
    },
    {
      title: t("NotificationSent"),
      render: (data: IUserGymNotificationWithGymInfo) => {
        if (data.dateOfSent) {
          return (
            <span style={{ color: "#32BC69" }}>
              {t("Yes")}({dayjs(data.dateOfSent).format("MMM D, YYYY")})
            </span>
          );
        } else {
          return <span style={{ color: "#D93131" }}>{t("No")}</span>;
        }
      },
    },
    {
      title: t("Action"),
      render: (data: IUserGymNotificationWithGymInfo) => {
        return (
          <div className={"action-wrapper"}>
            <img src={dots} alt={"action"} />
            <ul className={"dropdown"}>
              <li className={"dropdown-item"} onClick={() => updateSentDateHandler(data.id)}>
                <img src={editIcon} alt={"edit"} />
                {data.dateOfSent ? t("MarkAsUnSend") : t("MarkAsSent")}
              </li>
              <li className={"dropdown-item"} onClick={() => remove(data.id)}>
                <img style={{ width: "20px" }} src={basketIcon} alt={"delete"} />
                {t("Remove")}
              </li>
            </ul>
          </div>
        );
      },
    },
  ];

  return (
    <div className={"gyms-page"}>
      <MainTitle text={t("GymNotifications")} />

      <div className={"top-row"}>
        <Form
          className={"form"}
          ref={formRef}
          name="gym-notidication-filter"
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
        >
          <FormItemDatePicker
            dateFormat={"DD/MM/YYYY"}
            name={"from"}
            label={t("From")}
            rules={{ required: false }}
            style={{
              width: "30%",
            }}
          />
          <FormItemDatePicker
            dateFormat={"DD/MM/YYYY"}
            name={"to"}
            label={t("To")}
            rules={{ required: false }}
            style={{
              width: "30%",
            }}
          />
          <div className={"btn-wrapper"}>
            <CustomButton
              text={""}
              type={"submit"}
              typeStyle={"fill"}
              icon={searchIconWhite}
              altIcon={"search"}
            />
          </div>
        </Form>
      </div>

      <Table
        className={"table"}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={data.items}
        pagination={{ ...tableParams, total: totalCount }}
        loading={isLoading}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default ClientGymNotification;
